  "use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Briefcase,
  Building2,
  Bus,
  Cake,
  Car,
  ClipboardList,
  Cpu,
  Download,
  Factory,
  FileText,
  FolderOpen,
  GraduationCap,
  Handshake,
  Headphones,
  HelpCircle,
  Hotel,
  Images,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  MessagesSquare,
  Signpost,
  Monitor,
  Newspaper,
  Plane,
  Radio,
  Server,
  Shield,
  Share2,
  Smartphone,
  Store,
  Stethoscope,
  Tag,
  Tablet,
  Tv,
  UtensilsCrossed,
  Wifi,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { env } from "@/lib/environment";
import { Typography, Card, CalendlyWidget } from "@/components/ui";
import { NAVIGATION_MENU } from "@/lib/data/navigation";
import { useIsAdLanding } from "@/lib/hooks/useIsAdLanding";
import { NewButton } from "./ui/NewButton";

const NAV_ITEM_ICONS: Record<string, LucideIcon> = {
  Monitor,
  FileText,
  Shield,
  MessagesSquare,
  Smartphone,
  LayoutGrid,
  Server,
  Wifi,
  Headphones,
  Tablet,
  Tv,
  Building2,
  Store,
  UtensilsCrossed,
  Factory,
  Hotel,
  Stethoscope,
  Bus,
  GraduationCap,
  LayoutDashboard,
  Plane,
  ClipboardList,
  Car,
  LayoutTemplate,
  Radio,
  Share2,
  Briefcase,
  Cake,
  Newspaper,
  BookOpen,
  Images,
  FolderOpen,
  HelpCircle,
  Download,
  Tag,
  Handshake,
  Cpu,
  Signpost,
  // Backward compatible alias for navigation data
  City: Building2,
};

function NavMenuItemLeadingIcon({ name }: { name?: string }) {
  if (!name) return null;
  const Icon = NAV_ITEM_ICONS[name];
  if (!Icon) return null;
  return (
    <Icon
      className="h-4 w-4 shrink-0 text-text-secondary"
      strokeWidth={1.5}
      aria-hidden
    />
  );
}

export default function Header() {
  const isAdLanding = useIsAdLanding();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedMobileSection, setExpandedMobileSection] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Bail out during SSR just in case this ever moves outside "use client"
    if (typeof window === "undefined") return;

    const raf =
      window.requestAnimationFrame ||
      function (cb: (timestamp: number) => void) {
        return setTimeout(cb, 16) as unknown as number;
      };

    const getScrollY = () =>
      typeof window.scrollY === "number" ? window.scrollY : window.pageYOffset || 0;

    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      raf(() => {
        const y = getScrollY();
        setIsScrolled(y > 0);
        ticking = false;
      });
    };

    // Try passive; if not supported, browser will treat it as a normal listener
    window.addEventListener("scroll", onScroll, { passive: true });

    // Initialize once
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      // Reset expanded section when opening menu
      setExpandedMobileSection(null);
    }
  };
  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setExpandedMobileSection(null);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  // Handle dropdown open with immediate effect
  const handleDropdownEnter = (dropdown: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setActiveDropdown(dropdown);
  };

  // Handle dropdown close with delay to prevent flickering
  const handleDropdownLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (activeDropdown && !target.closest(".group")) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [activeDropdown]);

  const DesktopDropdown: React.FC<{
    menuKey: "products" | "customers" | "resources";
    label: string;
    dropdownId: string;
    isOpen: boolean;
  }> = ({ menuKey, label, dropdownId, isOpen }) => {
    const section = NAVIGATION_MENU[menuKey];
    const sectionOrder: Array<"LEFT" | "MIDDLE" | "RIGHT"> = ["LEFT", "MIDDLE", "RIGHT"];
    const widthClass = "w-auto";
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [dropdownTopPx, setDropdownTopPx] = useState<number | null>(null);

    const dedupeItems = (
      items:
        | Array<{
            href: string;
            label: string;
            className?: string;
            badge?: string;
            icon?: string;
            description?: string;
          }>
        | undefined
    ) => {
      const seen = new Set<string>();
      return (items ?? []).filter(it => {
        const key = `${it.href}|${it.label}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    // Determine which columns (sections) actually have content
    const visibleSections = sectionOrder.filter(sec =>
      section.items.some(cat => cat.section === sec)
    );

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveDropdown(null);
      } else if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (activeDropdown !== menuKey) {
          setActiveDropdown(menuKey);
          // Focus the first link in the dropdown after a brief delay
          setTimeout(() => {
            const firstLink = dropdownRef.current?.querySelector("a[href]") as HTMLAnchorElement;
            firstLink?.focus();
          }, 100);
        } else {
          // If dropdown is already open, move to first menu item
          const firstLink = dropdownRef.current?.querySelector("a[href]") as HTMLAnchorElement;
          firstLink?.focus();
        }
      } else if (e.key === "ArrowUp" && activeDropdown === menuKey) {
        e.preventDefault();
        setActiveDropdown(null);
      } else if (e.key === "Tab") {
        // Close dropdown on Tab but don't prevent default Tab behavior
        setActiveDropdown(null);
        // Don't prevent default - let Tab navigation continue naturally
      }
    };

    // Handle focus management when dropdown state changes
    // Note: We don't auto-focus the first link here to prevent blinking
    // Focus management is handled in the keyboard event handlers

    // Keep the dropdown aligned to the trigger vertically, but centered on screen horizontally.
    useEffect(() => {
      if (typeof window === "undefined") return;
      if (!isOpen) return;

      const updatePos = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        // 8px gap to match previous mt-2 spacing
        setDropdownTopPx(Math.round(rect.bottom + 8));
      };

      updatePos();
      window.addEventListener("resize", updatePos);
      window.addEventListener("scroll", updatePos, { passive: true });
      return () => {
        window.removeEventListener("resize", updatePos);
        window.removeEventListener("scroll", updatePos);
      };
    }, [isOpen]);

    return (
      <div className="group relative">
        <button
          ref={triggerRef}
          type="button"
          className={`flex items-center text-primary-text text-base  ${
            menuKey === "products" ? "px-1.5" : "px-0 xl:px-3"
          } py-[21px] font-medium transition-colors`}
          onMouseEnter={() => handleDropdownEnter(menuKey)}
          onMouseLeave={handleDropdownLeave}
          onFocus={() => handleDropdownEnter(menuKey)}
          onBlur={e => {
            // Close dropdown when button loses focus, but only if focus isn't moving to dropdown content
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!dropdownRef.current?.contains(relatedTarget)) {
              handleDropdownLeave();
            }
          }}
          onKeyDown={handleKeyDown}
          aria-haspopup="menu"
          aria-expanded={activeDropdown === menuKey}
          aria-controls={dropdownId}
        >
          <div className="flex py-1.5 px-3 rounded-md flex-row items-center hover:text-primary transition-colors duration-200">
            {label}
            <svg
              className="ml-0.5 w-4 h-4 mt-[-0.5px]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {/* Invisible bridge to prevent gap issues */}
        <div
          className="absolute left-0 right-0 h-2 top-full z-20"
          onMouseEnter={() => handleDropdownEnter(menuKey)}
          onMouseLeave={handleDropdownLeave}
          style={{ pointerEvents: activeDropdown === menuKey ? "auto" : "none" }}
          role="presentation"
        />

        <div
          ref={dropdownRef}
          id={dropdownId}
          className={`fixed left-1/2 -translate-x-1/2 flex items-stretch flex-row justify-between gap-3 ${widthClass} bg-white ${
            menuKey === "products" || menuKey === "customers" || menuKey === "resources"
              ? "shadow-default rounded-lg overflow-x-auto overflow-y-hidden p-0 max-w-[calc(100vw-32px)]"
              : "shadow-default rounded-lg p-4 pr-0"
          } transform-gpu will-change-[opacity,transform] transition-all duration-300 ease-in-out ${
            activeDropdown === menuKey
              ? "opacity-100 translate-y-0 visible pointer-events-auto"
              : "opacity-0 -translate-y-2 invisible pointer-events-none"
          }`}
          style={dropdownTopPx != null ? { top: dropdownTopPx } : undefined}
          onMouseEnter={() => handleDropdownEnter(menuKey)}
          onMouseLeave={handleDropdownLeave}
          onBlur={e => {
            // Close dropdown when it loses focus and focus isn't going to the button
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!triggerRef.current?.contains(relatedTarget)) {
              handleDropdownLeave();
            }
          }}
          onKeyDown={e => {
            if (e.key === "Escape") {
              setActiveDropdown(null);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              const links = Array.from(
                dropdownRef.current?.querySelectorAll("a[href]") || []
              ) as HTMLAnchorElement[];
              const currentIndex = Array.from(links).indexOf(
                document.activeElement as HTMLAnchorElement
              );
              const nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
              links[nextIndex]?.focus();
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              const links = Array.from(
                dropdownRef.current?.querySelectorAll("a[href]") || []
              ) as HTMLAnchorElement[];
              const currentIndex = Array.from(links).indexOf(
                document.activeElement as HTMLAnchorElement
              );
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
              links[prevIndex]?.focus();
            } else if (e.key === "Tab") {
              // Allow normal Tab navigation within dropdown
              // Don't prevent default - let Tab move to next/previous focusable element
            }
          }}
          role="menu"
          aria-label={`${label} menu`}
          tabIndex={-1}
        >
          {menuKey === "products" ? (
            (() => {
              const leftCategories = section.items.filter(cat => cat.section === "LEFT");
              const middleCategories = section.items.filter(cat => cat.section === "MIDDLE");

              const rightGroups = section.items.filter(cat => cat.section === "RIGHT");
              const promoItem = rightGroups
                .flatMap(g => g.items || [])
                .find(it => (it as any).src) as any;

              const viewAllProductsItem = section.items
                .flatMap(cat => cat.items || [])
                .find(it => /view all products/i.test((it as any).label)) as any;

              const renderCategoryBlock = (
                category: (typeof leftCategories)[number],
                catIndex: number,
                column: "LEFT" | "MIDDLE"
              ) => (
                <div
                  className="flex flex-col gap-1"
                  key={`products-${column}-cat-${catIndex}-${category.category || "misc"}`}
                >
                  {category.category && (
                    <Typography
                      as="p"
                      className={`text-[10px] font-normal tracking-wide uppercase text-[#6B6F7C] ${
                        column === "LEFT" ? "px-0 pt-1" : "px-0 pt-1"
                      }`}
                    >
                      {category.category}
                    </Typography>
                  )}
                  <ul className="space-y-5 w-full mt-3">
                    {dedupeItems(category.items)
                      .filter(it => !/view all products/i.test(it.label))
                      .map((item, index) => {
                        const isViewAll = /view all/i.test(item.label);
                        return (
                          <li
                            key={`products-${column}-item-${catIndex}-${index}-${item.label}`}
                            className={`flex w-full ${
                              isViewAll ? "border-t border-gray-200 pt-4 mt-1" : ""
                            }`}
                          >
                            <Link
                              href={item.href}
                              className={`${
                                isViewAll
                                  ? "group/viewall inline-flex items-center gap-3 text-primary font-bold"
                                  : "flex items-center justify-between w-full text-text-primary hover:text-primary"
                              } text-sm transition-colors duration-200 rounded-md`}
                              onClick={closeDropdown}
                              role="menuitem"
                              tabIndex={activeDropdown === menuKey ? 0 : -1}
                            >
                              <span className="flex items-center gap-2.5 min-w-0">
                                <NavMenuItemLeadingIcon name={item.icon} />
                                <span className="min-w-0">{item.label}</span>
                              </span>
                              {isViewAll ? (
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="shrink-0 text-primary transition-transform duration-200 group-hover/viewall:translate-x-1 group-hover/viewall:scale-125"
                                  aria-hidden
                                >
                                  <path
                                    d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              ) : null}
                            </Link>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              );

              return (
                <div className="flex w-full items-stretch ">
                  {/* Left + middle columns + bottom view-all row */}
                  <div className="flex-none w-fit p-8 pr-10">
                    <div className="flex items-start gap-12">
                      <div className="w-[220px] space-y-8">
                        {leftCategories.map((category, catIndex) =>
                          renderCategoryBlock(category, catIndex, "LEFT")
                        )}
                      </div>
                      <div className="w-[220px] space-y-8">
                        {middleCategories.map((category, catIndex) =>
                          renderCategoryBlock(category, catIndex, "MIDDLE")
                        )}
                      </div>
                    </div>

                    {viewAllProductsItem ? (
                      <div className="mt-6 border-t border-gray-200 pt-4">
                        <Link
                          href={viewAllProductsItem.href}
                          className="group/viewall inline-flex items-center gap-3 text-primary font-bold transition-colors duration-200 rounded-md"
                          onClick={closeDropdown}
                          role="menuitem"
                          tabIndex={activeDropdown === menuKey ? 0 : -1}
                        >
                          <span>{viewAllProductsItem.label}</span>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="shrink-0 text-primary transition-transform duration-200 group-hover/viewall:translate-x-1 group-hover/viewall:scale-125"
                            aria-hidden
                          >
                            <path
                              d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                              fill="currentColor"
                            />
                          </svg>
                        </Link>
                      </div>
                    ) : null}
                  </div>

                  {/* Promo card on right */}
                  <div className="w-[360px] border-l border-gray-100 bg-gray-50 p-6 self-stretch flex flex-col">
                    {promoItem ? (
                      <Link
                        href={promoItem.href}
                        onClick={closeDropdown}
                        role="menuitem"
                        tabIndex={activeDropdown === menuKey ? 0 : -1}
                        className="block w-full"
                      >
                        <Card
                          variant="elevated"
                          className="w-full bg-transparent p-0 text-left overflow-hidden rounded-none border-0 shadow-none cursor-pointer transition-shadow"
                        >
                          <Image
                            src={promoItem.src as string}
                            className="w-full rounded-lg"
                            alt={promoItem.label || "Promotional image"}
                            title={promoItem.label}
                            width={258}
                            height={189}
                          />
                          <div className="pt-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0">
                                <div className="text-text-primary flex flex-row items-center gap-2 justify-between font-semibold text-base leading-6 mb-3">
                                  {promoItem.label}
                                  <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden
                                >
                                  <path
                                    d="M12.1717 12.0005L9.34326 9.17203L10.7575 7.75781L15.0001 12.0005L10.7575 16.2431L9.34326 14.8289L12.1717 12.0005Z"
                                    fill="currentColor"
                                  />
                                </svg>
                                </div>
                                {promoItem.description ? (
                                  <p className="mt-1 text-sm text-[#6B6F7C] leading-5">
                                    {promoItem.description as string}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ) : null}
                  </div>
                </div>
              );
            })()
          ) : menuKey === "customers" ? (
            (() => {
              const leftCategories = section.items.filter(cat => cat.section === "LEFT");
              const middleCategories = section.items.filter(cat => cat.section === "MIDDLE");
              const rightCategories = section.items.filter(cat => cat.section === "RIGHT");

              const promoItem = rightCategories
                .flatMap(g => g.items || [])
                .find(it => (it as any).src) as any;

              const caseStudiesCategory = rightCategories.find(
                c => (c.category || "").toLowerCase() === "case studies"
              );

              const caseStudiesViewAllItem = section.items
                .flatMap(cat => cat.items || [])
                .find(it => /view all/i.test((it as any).label) && (it as any).href === "/case-studies") as
                | any
                | undefined;

              const renderCategoryBlock = (
                category: (typeof leftCategories)[number],
                catIndex: number,
                column: "LEFT" | "MIDDLE"
              ) => (
                <div
                  className="flex flex-col gap-1"
                  key={`customers-${column}-cat-${catIndex}-${category.category || "misc"}`}
                >
                  {category.category && (
                    <Typography
                      as="p"
                      className="text-[10px] font-normal tracking-wide uppercase text-[#6B6F7C] px-0 pt-1"
                    >
                      {category.category}
                    </Typography>
                  )}
                  <ul className="space-y-4 w-full mt-3">
                    {dedupeItems(category.items)
                      .filter(it => !(/view all/i.test(it.label) && it.href === "/case-studies"))
                      .map((item, index) => {
                        const isViewAll = /view all/i.test(item.label);
                        return (
                          <li
                            key={`customers-${column}-item-${catIndex}-${index}-${item.label}`}
                            className={`flex w-full ${
                              isViewAll ? "border-t border-gray-200 pt-4 mt-1" : ""
                            }`}
                          >
                            <Link
                              href={item.href}
                              className={`${
                                isViewAll
                                  ? "group/viewall inline-flex items-center gap-3 text-primary font-bold"
                                  : "flex items-center justify-between w-full text-text-primary hover:text-primary"
                              } text-sm transition-colors duration-200 rounded-md`}
                              onClick={closeDropdown}
                              role="menuitem"
                              tabIndex={activeDropdown === menuKey ? 0 : -1}
                            >
                              <span className="flex items-center gap-2.5 min-w-0">
                                {!isViewAll ? <NavMenuItemLeadingIcon name={item.icon} /> : null}
                                <span className="min-w-0">{item.label}</span>
                              </span>
                              {isViewAll ? (
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="shrink-0 text-primary transition-transform duration-200 group-hover/viewall:translate-x-1 group-hover/viewall:scale-125"
                                  aria-hidden
                                >
                                  <path
                                    d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              ) : null}
                            </Link>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              );

              return (
                <div className="flex w-full items-stretch">
                  {/* Left side */}
                  <div className="flex-none w-fit p-8 pr-10">
                    <div className="flex items-start gap-12">
                      <div className="w-[180px] space-y-8">
                        {leftCategories.map((category, catIndex) =>
                          renderCategoryBlock(category, catIndex, "LEFT")
                        )}
                      </div>
                      <div className="w-[180px] space-y-8">
                        {middleCategories.map((category, catIndex) =>
                          renderCategoryBlock(category, catIndex, "MIDDLE")
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right side */}
                  <div className="w-[360px] border-l border-gray-100 bg-gray-50 p-6 self-stretch flex flex-col">
                    {promoItem ? (
                      <Link
                        href={promoItem.href}
                        onClick={closeDropdown}
                        role="menuitem"
                        tabIndex={activeDropdown === menuKey ? 0 : -1}
                        className="block w-full"
                      >
                        <Card
                          variant="elevated"
                          className="w-full bg-transparent p-0 text-left overflow-hidden rounded-none border-0 shadow-none cursor-pointer transition-shadow"
                        >
                          <Image
                            src={promoItem.src as string}
                            className="w-full rounded-lg"
                            alt={promoItem.label || "Featured"}
                            title={promoItem.label}
                            width={258}
                            height={189}
                          />
                          <div className="pt-3">
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0">
                                <div className="text-text-primary flex flex-row items-center gap-2 justify-between font-semibold text-base leading-6">
                                  {promoItem.label}
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden
                                  >
                                    <path
                                      d="M12.1717 12.0005L9.34326 9.17203L10.7575 7.75781L15.0001 12.0005L10.7575 16.2431L9.34326 14.8289L12.1717 12.0005Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ) : null}

                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <Typography
                        as="p"
                        className="text-[10px] font-normal tracking-wider uppercase text-[#6B6F7C]"
                      >
                        Case Studies
                      </Typography>

                      <ul className="mt-4 space-y-4">
                        {(caseStudiesCategory?.items || [])
                          .filter(it => !/view all/i.test(it.label))
                          .map((item, idx) => (
                            <li key={`customers-case-${idx}-${item.label}`}>
                              <Link
                                href={item.href}
                                className="text-sm text-text-primary hover:text-primary transition-colors duration-200"
                                onClick={closeDropdown}
                                role="menuitem"
                                tabIndex={activeDropdown === menuKey ? 0 : -1}
                              >
                                {item.label}
                              </Link>
                            </li>
                          ))}
                      </ul>

                      {caseStudiesViewAllItem ? (
                        <div className="mt-4">
                          <Link
                            href={caseStudiesViewAllItem.href}
                            className="group/viewall inline-flex items-center gap-3 text-primary font-semibold transition-colors duration-200"
                            onClick={closeDropdown}
                            role="menuitem"
                            tabIndex={activeDropdown === menuKey ? 0 : -1}
                          >
                            <span>View all</span>
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="shrink-0 text-primary transition-transform duration-200 group-hover/viewall:translate-x-1 group-hover/viewall:scale-125"
                              aria-hidden
                            >
                              <path
                                d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                fill="currentColor"
                              />
                            </svg>
                          </Link>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })()
          ) : menuKey === "resources" ? (
            (() => {
              const leftCategories = section.items.filter(cat => cat.section === "LEFT");
              const rightCategories = section.items.filter(cat => cat.section === "RIGHT");

              const promoItem = rightCategories
                .flatMap(g => g.items || [])
                .find(it => (it as any).src) as any;

              const linkItems = (items: Array<{ href: string; label: string; icon?: string }> = []) =>
                items.map((item, idx) => (
                  <li key={`resources-item-${idx}-${item.label}`} className="flex w-full">
                    <Link
                      href={item.href}
                      className="flex items-center gap-3 text-sm text-text-primary hover:text-primary transition-colors duration-200"
                      onClick={closeDropdown}
                      role="menuitem"
                      tabIndex={activeDropdown === menuKey ? 0 : -1}
                    >
                      <NavMenuItemLeadingIcon name={item.icon} />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ));

              return (
                <div className="flex w-full items-stretch">
                  {/* Left side */}
                  <div className="p-8 pr-10">
                    <div className="flex items-start gap-12 w-[260px]">
                      {leftCategories.map((category, catIndex) => (
                        <div
                          className="flex flex-col gap-2"
                          key={`resources-left-${catIndex}-${category.category || "misc"}`}
                        >
                          {category.category ? (
                            <Typography
                              as="p"
                              className="text-[10px] font-normal tracking-wider uppercase text-[#6B6F7C]"
                            >
                              {category.category}
                            </Typography>
                          ) : null}
                          <ul className="space-y-4 mt-3">{linkItems(category.items)}</ul>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right side */}
                  <div className="w-[340px] border-l border-gray-100 bg-gray-50 p-6 self-stretch flex flex-col">
                    {promoItem ? (
                      <Link
                        href={promoItem.href}
                        onClick={closeDropdown}
                        role="menuitem"
                        tabIndex={activeDropdown === menuKey ? 0 : -1}
                        className="block w-full"
                      >
                        <Card
                          variant="elevated"
                          className="w-full bg-transparent p-0 text-left overflow-hidden rounded-none border-0 shadow-none cursor-pointer transition-shadow"
                        >
                          <Image
                            src={promoItem.src as string}
                            className="w-full rounded-lg"
                            alt={promoItem.label || "Featured"}
                            title={promoItem.label}
                            width={258}
                            height={189}
                          />
                          <div className="pt-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-text-primary font-semibold text-base leading-6">
                                {promoItem.label}
                              </div>
                              <div className="shrink-0 text-gray-400">
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden
                                >
                                  <path
                                    d="M12.1717 12.0005L9.34326 9.17203L10.7575 7.75781L15.0001 12.0005L10.7575 16.2431L9.34326 14.8289L12.1717 12.0005Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ) : null}

                    {rightCategories
                      .filter(cat => (cat.items || []).every(it => !(it as any).src))
                      .map((category, catIndex) => (
                        <div
                          className={`${promoItem ? "mt-6 border-t border-gray-200 pt-6" : ""}`}
                          key={`resources-right-${catIndex}-${category.category || "misc"}`}
                        >
                          {category.category ? (
                            <Typography
                              as="p"
                              className="text-[10px] font-normal tracking-wider uppercase text-[#6B6F7C]"
                            >
                              {category.category}
                            </Typography>
                          ) : null}
                          <ul className={`${category.category ? "mt-4" : ""} space-y-3`}>
                            {linkItems(category.items)}
                          </ul>
                        </div>
                      ))}
                  </div>
                </div>
              );
            })()
          ) : (
            visibleSections.map((sec, idx) => (
              <div
                className={`${
                  visibleSections.length === 2 ? "w-1/2" : "w-1/3"
                } ${sec === "RIGHT" ? "space-y-2" : "space-y-2"} ${
                  menuKey === "resources" ? "" : "px-4"
                } ${sec === "MIDDLE" ? "border-l border-gray-200 " : ""} ${
                  sec === "RIGHT" ? "bg-[#F6F7F9] p-2 rounded-lg" : ""
                }`}
                key={`${menuKey}-col-${sec}-${idx}`}
              >
                {/* RIGHT column promo card sourced from data (first RIGHT item with src) */}
                {sec === "RIGHT" &&
                  (() => {
                    const rightGroups = section.items.filter(cat => cat.section === "RIGHT");
                    const promoItem = rightGroups
                      .flatMap(g => g.items || [])
                      .find(it => (it as any).src);
                    if (!promoItem) return null;
                    return (
                      <Link
                        href={promoItem.href}
                        onClick={closeDropdown}
                        role="menuitem"
                        tabIndex={activeDropdown === menuKey ? 0 : -1}
                        className="block"
                      >
                        <Card
                          variant="elevated"
                          className="w-full bg-transparent p-0 text-left overflow-hidden rounded-none border-0 shadow-none cursor-pointer transition-shadow"
                        >
                          <Image
                            src={(promoItem as any).src as string}
                            className="w-full rounded-lg"
                            alt={promoItem.label || (promoItem as any).alt || "Promotional image"}
                            title={promoItem.label}
                            width={258}
                            height={189}
                          />
                          <div className="flex gap-3 flex-row py-2 justify-start items-center">
                            <span className="inline-block underline max-w-[80%] text-left">
                              {promoItem.label}
                            </span>
                            <div className="rounded-full w-6 h-6 items-center justify-between flex border border-gray-200">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12.1717 12.0005L9.34326 9.17203L10.7575 7.75781L15.0001 12.0005L10.7575 16.2431L9.34326 14.8289L12.1717 12.0005Z"
                                  fill="#605C63"
                                />
                              </svg>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    );
                  })()}

                {section.items
                  .filter(cat => cat.section === sec)
                  .map((category, catIndex) => (
                    <div
                      className={`flex flex-col gap-1 ${sec === "LEFT" ? "" : "px-4"} ${
                        sec === "RIGHT" && catIndex === 0 ? "" : ""
                      }`}
                      key={`${menuKey}-${sec}-cat-${catIndex}-${category.category || "misc"}`}
                    >
                      {category.category && (
                        <Typography
                          as="p"
                        className={`${
                          sec === "LEFT"
                            ? `font-medium text-base text-gray-500 px-3 pb-0 ${
                                catIndex === 0 ? "pt-1" : "pt-2"
                              }`
                            : "font-medium text-base text-gray-500 px-0 pb-0 pt-2"
                        }`}
                        >
                          {category.category}
                        </Typography>
                      )}
                      <ul className={`${sec === "RIGHT" ? "space-y-2 w-full" : "space-y-2 w-full"}`}>
                        {dedupeItems(
                          sec === "RIGHT" && (!category.category || category.category === undefined)
                            ? (category.items || []).filter(i => !(i as any).src)
                            : category.items
                        ).map((item, index) => {
                          const isViewAll = /view all/i.test(item.label);
                          return (
                            <li
                              key={`${menuKey}-${sec}-item-${catIndex}-${index}-${item.label}`}
                              className={`flex w-full ${sec === "MIDDLE" ? "py-3" : ""} ${
                                sec === "RIGHT" && !isViewAll ? "bg-white rounded-md" : ""
                              }`}
                            >
                              <Link
                                href={item.href}
                              className={`${
                                isViewAll
                                  ? "group/viewall flex items-center justify-start gap-4 font-bold"
                                  : "flex items-center justify-between"
                              } w-full transition-colors duration-200 ${
                                isViewAll ? "text-primary" : "text-text-primary hover:text-primary"
                              } text-sm ${item.className || ""}`}
                                onClick={closeDropdown}
                                role="menuitem"
                                tabIndex={activeDropdown === menuKey ? 0 : -1}
                              >
                                <span className="flex items-center gap-2.5 min-w-0">
                                  <NavMenuItemLeadingIcon name={item.icon} />
                                  <span className="min-w-0">{item.label}</span>
                                </span>
                                {isViewAll ? (
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="shrink-0 text-primary transition-transform duration-200 group-hover/viewall:translate-x-1 group-hover/viewall:scale-125"
                                    aria-hidden
                                  >
                                    <path
                                      d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                ) : (
                                  !isViewAll &&
                                  sec === "RIGHT" && (
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M12.1717 12.0005L9.34326 9.17203L10.7575 7.75781L15.0001 12.0005L10.7575 16.2431L9.34326 14.8289L12.1717 12.0005Z"
                                        fill="#605C63"
                                      />
                                    </svg>
                                  )
                                )}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };
  const handleMobileOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const anchor = target.closest("a");
    if (anchor) {
      const href = anchor.getAttribute("href");
      if (href && href !== "#") {
        closeMobileMenu();
      }
    }
  };

  const handleMobileOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      closeMobileMenu();
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a");
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (href && href !== "#") {
          closeMobileMenu();
        }
      }
    }
  };

  // Handle body scroll lock, Escape key, and focus for mobile menu
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!isMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    mobileMenuRef.current?.focus();
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        setIsMenuOpen(false);
        setExpandedMobileSection(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isMenuOpen]);

  return (
    <header
      className={`py-3.5 flex flex-row items-center lg:py-0 h-[68px] md:h-[90px] fixed top-[var(--announcement-bar-height,0px)] left-0 right-0 z-50 text-sm transition-all duration-300 ${
        isScrolled
          ? // ? "bg-white shadow-[0_5px_16px_rgba(0,0,0,0.1)] animate-[slideDown_0.35s_ease-out]"
            "bg-white"
          : "bg-white"
      }`}
    >
      <div className="container mx-auto px-8 xl:px-0">
        <div
          className={`flex flex-row w-full items-center justify-between transition-all duration-300`}
        >
          {/* Logo */}
          <div className="shrink-0">
            <Link href="/" className="flex items-center w-[156px] md:w-[161px]">
              <Image
                src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/pickcel-logo.svg`}
                alt="Pickcel"
                title="Pickcel"
                width={200}
                height={28}
                priority
                fetchPriority="high"
                sizes="120px"
                decoding="async"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-[3px]">
            <DesktopDropdown
              menuKey="products"
              label="Products"
              dropdownId="menu-products"
              isOpen={activeDropdown === "products"}
            />
            <DesktopDropdown
              menuKey="customers"
              label="Solutions"
              dropdownId="menu-customers"
              isOpen={activeDropdown === "customers"}
            />
            <DesktopDropdown
              menuKey="resources"
              label="Resources"
              dropdownId="menu-resources"
              isOpen={activeDropdown === "resources"}
            />

            <Link
              href="/pricing"
              className="text-primary-text text-base hover:text-primary px-1.5 py-[33px] font-medium transition-colors"
            >
              Pricing
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {!isAdLanding && (
              <Link
                href="https://console.pickcel.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-text bg-white text-base hover:text-primary px-2.5 py-2.5 rounded-lg font-medium transition-colors"
                data-mixpanel-id="header-login"
              >
                Login
              </Link>
            )}
            {!isAdLanding && (
              <Link
                href="https://console.pickcel.com/auth/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <NewButton variant="outlinePrimary" size="sm" data-mixpanel-id="header-start-free-trial">
                  Start Free Trial
                </NewButton>
              </Link>
            )}
            {isAdLanding ? (
              <Link
                href="/book-a-demo"
                className="px-4 py-2 rounded-lg font-medium text-primary inline-flex items-center justify-center text-base  border border-primary transition-colors"
                data-mixpanel-id="header-book-a-demo"
              >
                Book a Demo
              </Link>
            ) : null}
            <Link
              href="/company/contact"
              className="inline-flex"
              data-mixpanel-id="header-contact-us"
            >
              <NewButton variant="primary" size="sm">
                Contact Us
              </NewButton>
            </Link>
          </div>


          {/* Mobile center title */}
          <div className="flex items-center justify-between lg:hidden">
            {/* Mobile Menu Button */}
            <button
              type="button"
              onClick={toggleMenu}
              className="lg:hidden rounded-md text-gray-700 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {!isMenuOpen ? (
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="lg:hidden top-[calc(68px+var(--announcement-bar-height,0px))] md:top-[calc(90px+var(--announcement-bar-height,0px))] left-0 right-0 bottom-0 fixed z-10 px-6 bg-white py-3 outline-none"
            onClick={handleMobileOverlayClick}
            onKeyDown={handleMobileOverlayKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-menu-title"
            tabIndex={-1}
          >
            {/* Menu items */}
            <div className="flex flex-col h-full overflow-hidden scrollbar-none">
              <div className={`flex-1 overflow-y-auto ${expandedMobileSection ? 'max-h-[calc(100vh-250px)]' : ''}`}>
                <ul className="divide-y divide-gray-200">
                  {/* Products Section */}
                  <li>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-5 text-left hover:bg-primary/10 ${
                        expandedMobileSection === "products" ? "bg-[#FFF2EB] text-primary" : "bg-white"
                      }`}
                      onClick={() =>
                        setExpandedMobileSection(
                          expandedMobileSection === "products" ? null : "products"
                        )
                      }
                    >
                      <span
                        className={`text-base font-medium ${
                          expandedMobileSection === "products"
                            ? "text-primary"
                            : "text-text-primary"
                        }`}
                      >
                        Products
                      </span>
                      <svg
                        className={`w-5 h-5 transition-transform duration-200 ${
                          expandedMobileSection === "products" ? "rotate-180 stroke-primary" : "text-gray-400"
                        }`}
                        aria-hidden="true"
                        focusable="false"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {expandedMobileSection === "products" && (
                      <div className="pb-4 bg-gray-50">
                        <div className="space-y-4">
                          {/* Map LEFT categories */}
                          {NAVIGATION_MENU.products.items
                            .filter(cat => cat.section === "LEFT")
                            .map((category, catIndex) => (
                              <div
                                className="flex flex-col gap-1 space-y-0"
                                key={`mobile-products-left-${catIndex}-${
                                  category.category || "misc"
                                }`}
                              >
                                {category.category && (
                                  <Typography
                                    as="p"
                                    className={`font-medium text-lg text-gray-500 px-3 pb-0 ${
                                      catIndex === 0 ? "pt-2" : "pt-0"
                                    }`}
                                  >
                                    {category.category}
                                  </Typography>
                                )}
                                <ul className="space-y-0">
                                  {(category.items || []).map((item, index) => {
                                    const isViewAll = /view all/i.test(item.label);
                                    return (
                                      <li
                                        key={`mobile-products-left-item-${catIndex}-${index}-${item.label}`}
                                      >
                                        <Link
                                          href={item.href}
                                          className={`flex items-center ${isViewAll ? 'justify-start gap-4 font-bold' : 'justify-between font-medium'} w-full ${isViewAll ? 'text-primary' : 'text-primary-text'} text-base ${isViewAll ? '' : 'hover:text-primary'} px-3 py-2 transition-colors`}
                                        >
                                          <span className="flex items-center gap-2.5 min-w-0">
                                            <NavMenuItemLeadingIcon name={item.icon} />
                                            <span className="min-w-0">{item.label}</span>
                                          </span>
                                          {isViewAll && (
                                            <svg
                                              width="12"
                                              height="12"
                                              viewBox="0 0 12 12"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="shrink-0 text-primary"
                                              aria-hidden
                                            >
                                              <path
                                                d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                                fill="currentColor"
                                              />
                                            </svg>
                                          )}
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ))}

                          {/* Map RIGHT categories (e.g., Players) */}
                          {NAVIGATION_MENU.products.items
                            .filter(cat => cat.section === "RIGHT")
                            .map((category, catIndex) => (
                              <div
                                className="flex flex-col gap-1 space-y-0"
                                key={`mobile-products-right-${catIndex}-${
                                  category.category || "misc"
                                }`}
                              >
                                {category.category && (
                                  <Typography
                                    as="p"
                                    className={`font-medium text-lg text-gray-500 px-3 pb-0 ${
                                      catIndex === 0 ? "pt-2" : "pt-0"
                                    }`}
                                  >
                                    {category.category}
                                  </Typography>
                                )}
                                <ul className="space-y-0">
                                  {(category.items || []).map((item, index) => {
                                    const isViewAll = /view all/i.test(item.label);
                                    const hasImage = (item as any).src;

                                    // Special styling for "What is Digital Signage" (items with src)
                                    if (hasImage) {
                                      return (
                                        <li
                                          key={`mobile-products-right-item-${catIndex}-${index}-${item.label}`}
                                        >
                                          <Link
                                            href={item.href}
                                            className="flex items-center justify-start gap-4 w-full text-primary-text text-base hover:text-primary px-3 py-2 font-bold underline"
                                          >
                                            <span>{item.label}</span>
                                            <svg
                                              width="12"
                                              height="12"
                                              viewBox="0 0 12 12"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="shrink-0"
                                            >
                                              <path
                                                d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                                fill="#1A1B23"
                                              />
                                            </svg>
                                          </Link>
                                        </li>
                                      );
                                    }

                                    return (
                                      <li
                                        key={`mobile-products-right-item-${catIndex}-${index}-${item.label}`}
                                      >
                                        <Link
                                          href={item.href}
                                          className={`flex items-center ${isViewAll ? 'justify-start gap-4 font-bold' : 'justify-between font-medium'} w-full ${isViewAll ? 'text-primary' : 'text-primary-text'} text-base ${isViewAll ? '' : 'hover:text-primary'} px-3 py-2`}
                                        >
                                          <span className="flex items-center gap-2.5 min-w-0">
                                            <NavMenuItemLeadingIcon name={item.icon} />
                                            <span className="min-w-0">{item.label}</span>
                                          </span>
                                          {isViewAll && (
                                            <svg
                                              width="12"
                                              height="12"
                                              viewBox="0 0 12 12"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="shrink-0 text-primary"
                                              aria-hidden
                                            >
                                              <path
                                                d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                                fill="currentColor"
                                              />
                                            </svg>
                                          )}
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </li>

                  {/* Customers Section */}
                  <li>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-5 text-left bg-white hover:bg-primary/10 ${
                        expandedMobileSection === "customers" ? "bg-[#FFF2EB] text-primary" : ""
                      }`}
                      onClick={() =>
                        setExpandedMobileSection(
                          expandedMobileSection === "customers" ? null : "customers"
                        )
                      }
                    >
                      <span
                        className={`text-base font-medium ${
                          expandedMobileSection === "customers"
                            ? "text-primary"
                            : "text-text-primary"
                        }`}
                      >
                        Solutions
                      </span>
                      <svg
                        className={`w-5 h-5 transition-transform duration-200 ${
                          expandedMobileSection === "customers" ? "rotate-180 stroke-primary" : "text-gray-400"
                        }`}
                        aria-hidden="true"
                        focusable="false"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {expandedMobileSection === "customers" && (
                      <div className="pb-4 bg-gray-50">
                        <div className="space-y-4">
                          {/* Map all categories (LEFT, MIDDLE, RIGHT) */}
                          {(["LEFT", "MIDDLE", "RIGHT"] as const).map((sec) =>
                            NAVIGATION_MENU.customers.items
                              .filter(cat => cat.section === sec)
                              .map((category, catIndex) => (
                                <div
                                  className="flex flex-col gap-1 space-y-0"
                                  key={`mobile-customers-${sec}-${catIndex}-${
                                    category.category || "misc"
                                  }`}
                                >
                                  {category.category && (
                                    <Typography
                                      as="p"
                                      className={`font-medium text-lg text-gray-500 px-3 pb-0 ${
                                        catIndex === 0 ? "pt-2" : "pt-0"
                                      }`}
                                    >
                                      {category.category}
                                    </Typography>
                                  )}
                                  <ul className="space-y-0">
                                    {(category.items || []).map((item, index) => {
                                      const isViewAll = /view all/i.test(item.label);
                                      const hasImage = (item as any).src;

                                      // Special styling for promo link (e.g., "See How Pickcel Helped Amazon")
                                      if (hasImage) {
                                        return (
                                          <li
                                            key={`mobile-customers-${sec}-item-${catIndex}-${index}-${item.label}`}
                                          >
                                            <Link
                                              href={item.href}
                                              className={`flex items-center justify-start gap-4 w-full text-primary-text text-base hover:text-primary px-3 py-2 ${
                                                item.className || ""
                                              } font-bold underline`}
                                            >
                                              <span className="flex items-center gap-2.5 min-w-0">
                                                <NavMenuItemLeadingIcon name={item.icon} />
                                                <span className="min-w-0">{item.label}</span>
                                              </span>
                                              <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 12 12"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              className="shrink-0"
                                              >
                                                <path
                                                  d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                                  fill="#1A1B23"
                                                />
                                              </svg>
                                            </Link>
                                          </li>
                                        );
                                      }

                                      return (
                                        <li
                                          key={`mobile-customers-${sec}-item-${catIndex}-${index}-${item.label}`}
                                        >
                                          <Link
                                            href={item.href}
                                            className={`${
                                              isViewAll
                                                ? 'flex items-center justify-start gap-4 font-bold'
                                                : 'flex items-center justify-between font-medium'
                                            } w-full ${isViewAll ? 'text-primary' : 'text-primary-text'} text-base ${
                                              isViewAll ? '' : 'hover:text-primary'
                                            } px-3 py-2 ${item.className || ""}`}
                                          >
                                            <span className="flex items-center gap-2.5 min-w-0">
                                              <NavMenuItemLeadingIcon name={item.icon} />
                                              <span className="min-w-0">{item.label}</span>
                                            </span>
                                            {isViewAll && (
                                              <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 12 12"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="shrink-0 text-primary"
                                                aria-hidden
                                              >
                                                <path
                                                  d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                                  fill="currentColor"
                                                />
                                              </svg>
                                            )}
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ))
                          )}
                        </div>
                      </div>
                    )}
                  </li>

                  {/* Resources Section */}
                  <li>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-5 text-left bg-white hover:bg-primary/10 ${
                        expandedMobileSection === "resources" ? "bg-[#FFF2EB] text-primary" : ""
                      }`}
                      onClick={() =>
                        setExpandedMobileSection(
                          expandedMobileSection === "resources" ? null : "resources"
                        )
                      }
                    >
                      <span
                        className={`text-base font-medium ${
                          expandedMobileSection === "resources"
                            ? "text-primary"
                            : "text-text-primary"
                        }`}
                      >
                        Resources
                      </span>
                      <svg
                        className={`w-5 h-5 transition-transform duration-200 ${
                          expandedMobileSection === "resources" ? "rotate-180 stroke-primary" : "text-gray-400"
                        }`}
                        aria-hidden="true"
                        focusable="false"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {expandedMobileSection === "resources" && (
                      <div className="pb-4 bg-gray-50">
                        <div className="space-y-4">
                          {/* Map all categories (LEFT, RIGHT) */}
                          {(["LEFT", "RIGHT"] as const).map((sec) =>
                            NAVIGATION_MENU.resources.items
                              .filter(cat => cat.section === sec && cat.category)
                              .map((category, catIndex) => (
                                <div
                                  className="flex flex-col gap-1 space-y-0"
                                  key={`mobile-resources-${sec}-${catIndex}-${
                                    category.category || "misc"
                                  }`}
                                >
                                  {category.category && (
                                    <Typography
                                      as="p"
                                      className={`font-medium text-lg text-gray-500 px-3 pb-0 ${
                                        catIndex === 0 ? "pt-2" : "pt-0"
                                      }`}
                                    >
                                      {category.category}
                                    </Typography>
                                  )}
                                  <ul className="space-y-0">
                                    {(category.items || []).map((item, index) => {
                                      const isViewAll = /view all/i.test(item.label);
                                      return (
                                        <li
                                          key={`mobile-resources-${sec}-item-${catIndex}-${index}-${item.label}`}
                                        >
                                          <Link
                                            href={item.href}
                                            className={`flex items-center ${isViewAll ? 'justify-start gap-4 font-bold' : 'justify-between font-medium'} w-full ${isViewAll ? 'text-primary' : 'text-primary-text'} text-base ${isViewAll ? '' : 'hover:text-primary'} px-3 py-2`}
                                          >
                                            <span className="flex items-center gap-2.5 min-w-0">
                                              <NavMenuItemLeadingIcon name={item.icon} />
                                              <span className="min-w-0">{item.label}</span>
                                            </span>
                                            {isViewAll && (
                                              <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 12 12"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="shrink-0 text-primary"
                                                aria-hidden
                                              >
                                                <path
                                                  d="M8.78109 5.33327L5.20509 1.75726L6.14789 0.814453L11.3334 5.99993L6.14789 11.1853L5.20509 10.2425L8.78109 6.6666H0.666687V5.33327H8.78109Z"
                                                  fill="currentColor"
                                                />
                                              </svg>
                                            )}
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ))
                          )}
                        </div>
                      </div>
                    )}
                  </li>

                  {/* Pricing Link */}
                  <li>
                    <Link
                      href="/pricing"
                      className="flex items-center px-4 py-5 bg-white hover:bg-primary/10 text-base text-text-primary font-medium hover:text-primary">
                        Pricing
                    </Link>
                  </li>

                  {/* Mobile CTAs - inside menu when no dropdown expanded */}
                  {!expandedMobileSection && (
                    <li className="pt-6 pb-4">
                      <div className="flex flex-row gap-3">
                        {isAdLanding ? (
                          <>
                            <Link href="/company/contact" target="_self" data-mixpanel-id="header-mobile-contact-us" className="flex-1">
                              <NewButton variant="outlinePrimary" size="sm" className="w-full text-sm">
                                Contact Us
                              </NewButton>
                            </Link>
                            <Link href="/book-a-demo" target="_self" data-mixpanel-id="header-mobile-book-a-demo" className="flex-1">
                              <NewButton variant="primary" size="sm" className="w-full text-sm">
                                Book a Demo
                              </NewButton>
                            </Link>
                          </>
                        ) : (
                          <>
                            <Link
                              href="https://console.pickcel.com/m/"
                              target="_blank"
                              rel="noopener noreferrer"
                              data-mixpanel-id="header-mobile-login"
                              className="flex-1"
                            >
                              <NewButton variant="primary" size="sm" className="w-11/12 text-sm">
                                Login
                              </NewButton>
                            </Link>
                            <Link href="/book-a-demo" target="_self" data-mixpanel-id="header-mobile-book-a-demo" className="flex-1">
                              <NewButton variant="outlinePrimary" size="sm" className="w-11/12 text-sm">
                                Book a Demo
                              </NewButton>
                            </Link>
                          </>
                        )}
                      </div>
                    </li>
                  )}
                </ul>
              </div>
              {/* Mobile CTAs - sticky at bottom when dropdown expanded */}
              {expandedMobileSection && (
              <div className="shrink-0 pt-8 border-t border-gray-200 bg-white flex flex-row gap-3">
                {isAdLanding ? (
                  <>
                    <Link href="/company/contact" target="_self" data-mixpanel-id="header-mobile-contact-us" className="flex-1">
                      <NewButton variant="outlinePrimary" size="sm" className="w-11/12 text-sm">
                        Contact Us
                      </NewButton>
                    </Link>
                    <Link href="/book-a-demo" target="_self" data-mixpanel-id="header-mobile-book-a-demo" className="flex-1">
                      <NewButton variant="primary" size="sm" className="w-11/12 text-sm">
                        Book a Demo
                      </NewButton>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      href="https://console.pickcel.com/m"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-mixpanel-id="header-mobile-login"
                      className="flex-1"
                    >
                      <NewButton variant="primary" size="sm" className="w-11/12 text-sm">
                        Login
                      </NewButton>
                    </Link>
                    <Link href="/book-a-demo" target="_self" data-mixpanel-id="header-mobile-book-a-demo" className="flex-1">
                      <NewButton variant="outlinePrimary" size="sm" className="w-11/12 text-sm">
                        Book a Demo
                      </NewButton>
                    </Link>
                  </>
                )}
              </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
